import { Box, FormControlLabel, Link, Radio, RadioGroup, styled, Typography } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { setNewMortgageAction } from "../../../contexts/mortgage/Actions";
import { useMortgageDispatch, useMortgageState } from "../../../contexts/mortgage/Reducer";
import { LoadingState } from "../../../model/LoadingState";
import { Mortgage } from "../../../model/MortgageExtensionModel";
import { AnalyticsActivity, AppComponent } from "../../../services/event-service/EventService";
import { useEventService } from "../../../services/event-service/EventServiceContext";
import LifeInsurancePopupScreen from "../popups/life-insurance/LifeInsurancePopupScreen";

const MortgageTextLabel = styled(Typography)(() => ({
    color: "#333333",
    fontSize: "18px",
    letterSpacing: 0,
    lineHeight: "26px",
    fontWeight: 300,
}));

const WholeLifeInsuranceTitle = styled(Typography)(({ theme }) => ({
    color: "#333333",
    fontSize: "18px",
    fontWeight: "500",
    letterSpacing: 0,
    lineHeight: "30px",
}));

const WholeLifeInsuranceWrapper = styled(Box)(({ theme }) => ({
    marginTop: "16px",
    [theme.breakpoints.up("lg")]: {
        marginTop: "32px",
    },
}));

const DetailsLink = styled(Link)(({ theme }) => ({
    color: "#0093D1",
    ariaHasPopup: "dialog",
    cursor: "pointer",
}));

const AgreementButtonGroup = styled(RadioGroup)(({ theme }) => ({
    marginTop: "16px",
    [theme.breakpoints.up("lg")]: {
        marginTop: "24px",
    },
}));

const RadioLabel = styled(FormControlLabel)(({ theme }) => ({
    color: "#333333",
    fontWeight: 400,
    fontSize: "16px",
    letterSpacing: 0,
    lineHeight: "20px",
}));

const WholeLifeInsurance = (props: { loadingState: LoadingState; setRespondedToOffer: (value: boolean) => void }) => {
    const { t } = useTranslation();
    const { loadingState, setRespondedToOffer } = props;
    const { newMortgage } = useMortgageState();
    const mortgageDispatch = useMortgageDispatch();
    const eventService = useEventService();

    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

    const showDetailsDialog = () => {
        eventService.sendAnalyticsEvent(AnalyticsActivity.SHOW_INSURANCE_OFFER_DETAILS, AppComponent.SUMMARY_SCREEN);
        setDetailsDialogOpen(true);
    };

    const closeDetailsDialog = () => {
        setDetailsDialogOpen(false);
    };

    const handleAgreement = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = (event.target as HTMLInputElement).value;

        if (newMortgage) {
            const editedMortgage: Mortgage = {
                ...newMortgage,
                includeLifeInsuranceOffer: value === "yes" ? true : false,
            };
            eventService.sendAnalyticsEvent(
                editedMortgage.includeLifeInsuranceOffer
                    ? AnalyticsActivity.INSURANCE_OFFER_ACCEPTED
                    : AnalyticsActivity.INSURANCE_OFFER_DECLINED,
                AppComponent.SUMMARY_SCREEN
            );
            mortgageDispatch(setNewMortgageAction(editedMortgage));
            setRespondedToOffer(true);
        }
    };

    return (
        <WholeLifeInsuranceWrapper data-cy={"whole-life-insurance"}>
            <Box sx={{ marginBottom: "32px" }}>
                <WholeLifeInsuranceTitle>{t("SummaryScreen.Collective whole life insurance")}</WholeLifeInsuranceTitle>
                <MortgageTextLabel>{t("SummaryScreen.The death coverage for homeowners")}</MortgageTextLabel>
            </Box>
            <Box sx={{ marginBottom: "32px" }}>
                <MortgageTextLabel>
                    {t("SummaryScreen.Help your family bridge financial bottlenecks in the event of an emergency.")}{" "}
                    &nbsp;
                    <DetailsLink underline="none" onClick={showDetailsDialog} data-cy={"details-link"} role={"button"}>
                        {t("Actions.Show details")}
                    </DetailsLink>
                </MortgageTextLabel>
            </Box>
            <Box>
                <MortgageTextLabel>
                    {t(
                        "SummaryScreen.The collective whole life insurance offer is only valid once as part of this extension and cannot be offered in future."
                    )}
                </MortgageTextLabel>
            </Box>

            <AgreementButtonGroup name="agreement-buttons" onChange={handleAgreement}>
                <RadioLabel
                    value="yes"
                    control={<Radio disabled={loadingState === LoadingState.Loading} />}
                    label={t("SummaryScreen.Yes, I am interested. Please contact me.")}
                    data-cy={"radio-button-interested"}
                />
                <RadioLabel
                    value="no"
                    control={<Radio disabled={loadingState === LoadingState.Loading} />}
                    label={t("SummaryScreen.No, I irrevocably reject this offer.")}
                    data-cy={"radio-button-not-interested"}
                />
            </AgreementButtonGroup>

            <LifeInsurancePopupScreen open={detailsDialogOpen} closeDialogFunction={closeDetailsDialog} />
        </WholeLifeInsuranceWrapper>
    );
};

export default WholeLifeInsurance;
